import { css } from '@emotion/core';
import {
  useGetSubscriptionAddOnsStatuses,
  useGetTrialHistory,
} from 'apis/trial-subscription/queries';
import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentLocation } from 'redux/actions/location';
import { SubscriptionAddOnFeatureEnumMap } from './constants';
import {
  SubscriptionAddOnEnums,
  SubscriptionAddOnStatus,
} from '@weave/schema-gen-ts/dist/schemas/subscription-manager-service/v1/subscription_manager_service.pb';
import { selectFeatures } from 'redux/actions/customization/customization.action';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { TrialState } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/location_features.pb';
import { FeatureTrialData } from './types';
import { formatDate, getDaysDifference } from 'helpers/date';
import { SkeletonLoader } from '@weave/design-system';
import { TrialCard } from './trial-card';
import { RequestSource } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';

const STATUS_ORDER: Record<string, number> = {
  [SubscriptionAddOnStatus.ACTIVE]: 0,
  [SubscriptionAddOnStatus.FREE_TRIAL_ACTIVE]: 1,
  [SubscriptionAddOnStatus.FREE_TRIAL_ELIGIBLE]: 2,
  [SubscriptionAddOnStatus.ADDON_STATUS_UNSPECIFIED]: 3,
};

export const TrialCardList: FC = () => {
  const locationId = useSelector(selectCurrentLocation)?.LocationID;
  const customizationFeatures = useSelector(selectFeatures);

  const { data: addOnsList, isLoading: isLoadingAddOns } =
    useGetSubscriptionAddOnsStatuses({
      locationId,
      requestSource: RequestSource.REQUEST_SOURCE_WAM,
    });

  const { data: trialExpiredFeatureMap, isLoading: isLoadingTrialExpireHistory } =
    useGetTrialHistory(
      {
        locationId,
      },
      {
        select: (response) => {
          return response.trialState?.['FREE_TRIAL_EXPIRED'].features?.reduce<
            Partial<Record<Feature, TrialState>>
          >((acc, featureTrialExpiryData) => {
            if (featureTrialExpiryData.featureEnum) {
              acc[featureTrialExpiryData.featureEnum] = featureTrialExpiryData;
            }
            return acc;
          }, {});
        },
      }
    );

  const featureTrialDataList = useMemo<FeatureTrialData[]>(() => {
    return (addOnsList?.addons ?? [])
      .reduce<FeatureTrialData[]>((acc, { addonEnum, addonStatus }) => {
        if (!addonEnum || !addonStatus) return acc;
        const featureEnum =
          SubscriptionAddOnFeatureEnumMap[addonEnum as SubscriptionAddOnEnums];

        if (!featureEnum) return acc;

        const trialExpiredDate = trialExpiredFeatureMap?.[featureEnum]?.endDate;
        const trialEndDate = customizationFeatures.find(
          (feature) => feature.featureEnum === featureEnum
        )?.endDate;

        const daysLeftInTrial = trialEndDate
          ? getDaysDifference(trialEndDate, new Date()) + 1
          : undefined;
        acc.push({
          featureEnum,
          addonEnum,
          addonStatus,
          trialExpiredDate: formatDate(trialExpiredDate, 'MM/DD/YYYY'),
          trialEndDate: formatDate(trialEndDate, 'MM/DD/YYYY'),
          daysLeftInTrial,
        });

        return acc;
      }, [])
      .sort((a, b) => {
        const orderA = STATUS_ORDER[a.addonStatus] ?? 4;
        const orderB = STATUS_ORDER[b.addonStatus] ?? 4;
        return orderA - orderB;
      });
  }, [addOnsList, customizationFeatures, trialExpiredFeatureMap]);

  if (isLoadingAddOns || isLoadingTrialExpireHistory)
    return (
      <div css={styles.list}>
        {Array(5)
          .fill(0)
          .map((_, index) => (
            <SkeletonLoader key={index} height={72} width={511} />
          ))}
      </div>
    );

  return (
    <div css={styles.list}>
      {featureTrialDataList.map((data) => (
        <TrialCard key={data.featureEnum} {...data} />
      ))}
    </div>
  );
};

const styles = {
  list: css`
    display: flex;
    flex-direction: column;
    gap: 16px;
  `,
};
